import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { GameStateProvider, useGameState } from '../../components/WorkingChessLogic/GameStateContext';
import useGameLogic from '../../components/WorkingChessLogic/UseGameState';
import { connect } from 'react-redux';
import Select from 'react-select';
import { useHistory, useLocation } from 'react-router-dom';

// Import only the components we need
import BoardThree from '../../components/WorkingChessLogic/3Board.jsx';
import MovesThree from '../../components/WorkingChessLogic/3Moves.jsx';
import MobileMoves, { MobileNavigationControls, MobileMovesEngine } from '../components/MobileMoves.jsx';
import SetupDefaults from '../../components/WorkingChessLogic/SetupDefaults.js';
import ChessAnalysisWrapper from '../../util/ChessAnalysisWrapper';
import { ChessSocialContext } from '../../util/ChessSocialContext';
import { isNativeApp } from '../../util/platformDetection';

// Import utilities and functions from web version
import constants, { TAGS, DIFFICULTY } from '../../util/constants';
import { createStartPosition } from '../../util/PositionData';
import { postGame } from '../../util/api/game';
import { isProfane, cleanProfanity } from '../../util/filterProfanity';
import { checkRateLimit } from '../../util/rateLimiter';
import { getRateLimit, buildGlobalRateKey } from '../../util/rateLimitConfig';
import { collection, query, where, getDocs, doc, addDoc, setDoc } from 'firebase/firestore';
import { firestore } from '../../firebase';
import useStockfishAnalysisManager from '../../hooks/useStockfishAnalysisManager';
import stockfishEngine from '../../util/StockfishEngine.js';
import GameModeChooserModal from '../../components/modals/GameModeChooserModal';
import FENBuilderModal from '../../components/modals/FENBuilderModal';
import MobilePGNUploadModal from '../components/MobilePGNUploadModal';
import NavigationConfirmModal from '../../components/modals/NavigationConfirmModal';
import useUnsavedGameData from '../../hooks/useUnsavedGameData';
import FAQData from '../../constants/FAQData';
import AddGameFAQData from '../../constants/AddGameFAQData';

// Mobile-specific layout component
const MobileAddGameContent = ({ user }) => {
  const { state, dispatch } = useGameState();
  const { undoMove } = useGameLogic();
  const history = useHistory();
  const location = useLocation();
  const boardRef = useRef(null);
  const [boardSize, setBoardSize] = useState(350);
  const [activeTab, setActiveTab] = useState('board');
  
  // Core states needed for the components
  const [moveContent, setMoveContent] = useState('');
  const [variationContent, setVariationContent] = useState('');
  
  // Get current move and starred moves from state
  const currentMoveIdx = state.currentMove || '-1';
  const starredMoves = state.starredMoves || [];
  const moves = state.moves || [];
  const orientation = state.orientation || 'white';
  
  // Game metadata states
  const [gameCaption, setGameCaption] = useState('');
  const [tags, setTags] = useState([]);
  const [selectedDifficulty, setSelectedDifficulty] = useState(() => {
    return localStorage.getItem('preferredDifficulty') || 'All Levels';
  });
  const [gameMetadata, setGameMetadata] = useState({
    visibility: 'Public',
    isPrivate: false,
    gameMode: undefined
  });
  
  // Player data state (matching web version)
  const [playerData, setPlayerData] = useState({
    povPlayer: ['', ''],
    oppositePlayer: ['', ''],
  });
  
  // Bookmark states
  const [userFolders, setUserFolders] = useState([]);
  const [selectedBookmarkFolder, setSelectedBookmarkFolder] = useState('NONE');
  const [isCreatingNewFolder, setIsCreatingNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderPrivate, setNewFolderPrivate] = useState(false);
  
  // Modal states
  const [openModalType, setOpenModalType] = useState('');
  const [showPGNUploadModal, setShowPGNUploadModal] = useState(false);
  const [showBoardSetupConfirm, setShowBoardSetupConfirm] = useState(false);
  const [boardSetupPuzzleMode, setBoardSetupPuzzleMode] = useState(false);
  
  // Share tab step state
  const [shareStep, setShareStep] = useState(1); // 1 = required fields, 2 = optional fields
  const [showControlPanel, setShowControlPanel] = useState(false);
  const [showUndoModal, setShowUndoModal] = useState(false);
  const [undoMode, setUndoMode] = useState(false);
  const [deleteMode, setDeleteMode] = useState(false);
  const [showFAQModal, setShowFAQModal] = useState(false);
  const [expandedVideoFAQ, setExpandedVideoFAQ] = useState(false);
  
  // Optional field visibility states
  const [showDifficultyField, setShowDifficultyField] = useState(false);
  const [showWhitePlayerField, setShowWhitePlayerField] = useState(false);
  const [showBlackPlayerField, setShowBlackPlayerField] = useState(false);
  const [showBookmarkField, setShowBookmarkField] = useState(false);
  
  // Control panel states (matching web version)
  const [localShowStockfish, setLocalShowStockfish] = useState(() => {
    return localStorage.getItem('showStockfish') === 'true';
  });
  const [localShowStockfishEval, setLocalShowStockfishEval] = useState(() => {
    return localStorage.getItem('showStockfishEval') === 'true';
  });
  
  // Annotation tool states
  const [toolMode, setToolMode] = useState('NORMAL'); // NORMAL, DELETE, SQUARE, ARROW, TEXT
  const [selectedColor, setSelectedColor] = useState(constants.CHESSBOARD.COLOR_VARIANTS.RED); // Default pink
  const [arrowStartSquare, setArrowStartSquare] = useState(null);
  
  // Board customization states from MobileMovesEngine
  const [mobileActiveColor, setMobileActiveColor] = useState(undefined);
  const [mobileArrowMode, setMobileArrowMode] = useState(false);
  const [mobileArrowColor, setMobileArrowColor] = useState(undefined);
  const [mobileArrowStart, setMobileArrowStart] = useState(null);
  
  // Move annotation states
  const [showAnnotationPopup, setShowAnnotationPopup] = useState(false);
  const [popupContent, setPopupContent] = useState('');
  
  // Get context values
  const { isPuzzleMode, setIsPuzzleMode } = useContext(ChessSocialContext);
  
  // Navigation protection - detect unsaved game data
  const hasUnsavedData = useUnsavedGameData(moves, tags, gameCaption);
  
  // Expose unsaved data status globally for bottom navigation access
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.mobileAddGameHasUnsavedData = hasUnsavedData;
    }
  }, [hasUnsavedData]);

  // Navigation blocking when user has unsaved data
  // Note: Disabled history blocking to prevent interference with bottom navigation
  // Bottom navigation handles the confirmation modal directly
  /*
  useEffect(() => {
    if (!hasUnsavedData) return;
    
    const unblock = history.block((location, action) => {
      // Don't block if modal is already showing
      if (showNavigationConfirm) return true;
      
      // Store the pending navigation
      setPendingNavigation({ location, action });
      setShowNavigationConfirm(true);
      
      // Block the navigation for now
      return false;
    });
    
    return unblock;
  }, [hasUnsavedData, history, showNavigationConfirm]);
  */

  // Browser refresh/close protection
  useEffect(() => {
    if (!hasUnsavedData) return;
    
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = 'You will lose all moves, tags, and captions. Are you sure?';
      return 'You will lose all moves, tags, and captions. Are you sure?';
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedData]);
  
  // Add mobile-specific FEN handling (similar to web version)
  const [pendingFen, setPendingFen] = useState(null);
  const [rootFen, setRootFen] = useState(null);
  
  // Mobile equivalent of onRootFenChange
  const onMobileRootFenChange = useCallback((newFen) => {
    // console.log('🔥 [Mobile AddGame] onMobileRootFenChange called with:', newFen);
    setRootFen(newFen);
    
    // Clear any existing moves when setting new root FEN
    dispatch({ type: 'SET_MOVES', payload: [] });
    
    // Update the board directly if possible
    if (boardRef.current && boardRef.current.updateBoardFEN) {
      // console.log('🔥 [Mobile AddGame] Updating board directly via ref');
      boardRef.current.updateBoardFEN(newFen);
    }
    
    // Also dispatch to GameStateContext
    dispatch({ 
      type: 'INITIALIZE_GAME',
      payload: {
        fen: newFen,
        orientation: 'white',
        moves: [],
        starredMoves: [],
        startPosition: {
          fen: newFen,
          content: '',
          arrows: [],
          highlightedSquares: {},
          variations: []
        }
      }
    });
  }, [dispatch]);

  // Detect iPad/tablet - check multiple conditions
  const detectIsTablet = () => {
    // Check screen width
    const widthCheck = window.innerWidth >= 768;
    
    // Check if it's an iPad via user agent
    const isIPad = /iPad/i.test(navigator.userAgent) || 
                   (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    
    // Check aspect ratio for tablet-like screens
    const aspectRatio = window.innerWidth / window.innerHeight;
    const isTabletAspectRatio = aspectRatio > 0.65 && aspectRatio < 1.5 && 
                                Math.min(window.innerWidth, window.innerHeight) >= 600;
    
    // Debug logging for development
    console.log('Tablet Detection (AddGame):', {
      width: window.innerWidth,
      height: window.innerHeight,
      widthCheck,
      isIPad,
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      maxTouchPoints: navigator.maxTouchPoints,
      aspectRatio,
      isTabletAspectRatio,
      result: widthCheck || isIPad || isTabletAspectRatio
    });
    
    return widthCheck || isIPad || isTabletAspectRatio;
  };

  // Tablet detection state
  const [isTablet, setIsTablet] = useState(detectIsTablet());
  
  // Calculate board size based on screen dimensions
  useEffect(() => {
    const updateBoardSize = () => {
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;
      const padding = 20;
      // Account for header, tabs, and other UI elements
      const availableHeight = screenHeight - 60 - 50 - 80 - 60; // header + tabs + margins + bottom nav
      const maxBoardSize = Math.min(screenWidth - padding * 2, availableHeight * 0.6); // Use 60% of available height for board
      setBoardSize(Math.max(Math.min(maxBoardSize, 400), 280)); // Match ViewGameMobile: minimum 280px, max 400px
    };

    const updateTabletDetection = () => {
      const isTabletDevice = detectIsTablet();
      setIsTablet(isTabletDevice);
    };

    const handleResize = () => {
      updateBoardSize();
      updateTabletDetection();
    };
    
    updateBoardSize();
    updateTabletDetection();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Stockfish analysis state for mobile
  const [stockfishInitialized, setStockfishInitialized] = useState(false);
  const [hasAnalysisCache, setHasAnalysisCache] = useState(false);
  const [currentAnalysisCache, setCurrentAnalysisCache] = useState(null);
  
  // Cache functions (placeholder for now)
  const getCachedAnalysisForFEN = useCallback((fen) => {
    return null;
  }, []);
  
  const checkForCachedAnalysis = useCallback((moveId, fen) => {
    return null;
  }, []);

  // Use Stockfish analysis manager 
  const {
    stockfishAnalysis,
    stockfishIsAnalyzing,
    stockfishManager,
    handleStockfishAnalysisRequest
  } = useStockfishAnalysisManager(
    state.moves || [],
    state.currentMove,
    [getCachedAnalysisForFEN, checkForCachedAnalysis]
  );

  // Initialize Stockfish engine for mobile analysis
  useEffect(() => {
    const initializeStockfish = async () => {
      try {
        if (stockfishEngine && !stockfishInitialized) {
          await stockfishEngine.initialize();
          setStockfishInitialized(true);
          
          // Expose analysis cache for the engine
          window.currentAnalysisCache = currentAnalysisCache;
          window.stockfishAnalysisManager = stockfishManager;
          
          // Add event listener for analysis requests from the board
          const handleAnalysisRequest = (event) => {
            if (event.detail && event.detail.fen) {
              handleStockfishAnalysisRequest(event.detail.fen);
            }
          };
          
          window.addEventListener('request-stockfish-analysis', handleAnalysisRequest);
          
          return () => {
            window.removeEventListener('request-stockfish-analysis', handleAnalysisRequest);
            window.currentAnalysisCache = null;
            window.stockfishAnalysisManager = null;
          };
        }
      } catch (error) {
        console.error('Failed to initialize Stockfish engine for mobile AddGame:', error);
      }
    };

    initializeStockfish();
  }, [stockfishInitialized, currentAnalysisCache, stockfishManager, handleStockfishAnalysisRequest]);

  // Handle FEN Builder data on component mount - use ref to prevent duplicate processing
  const processedFENBuilderDataRef = useRef(false);
  
  useEffect(() => {
    const fenBuilderData = location.state;
    if (fenBuilderData && fenBuilderData.fromFENBuilder && !processedFENBuilderDataRef.current) {
      // console.log('🔥 [Mobile AddGame] Processing FEN Builder data:', fenBuilderData);
      
      // Ensure we have the dispatch function ready
      if (!dispatch) {
        // console.warn('🔥 [Mobile AddGame] Dispatch not ready, skipping FEN builder initialization');
        return;
      }
      
      // Mark as processed immediately to prevent double processing
      processedFENBuilderDataRef.current = true;
      
      if (fenBuilderData.customFEN) {
        // FEN flow - use single method to avoid conflicts
        // console.log('🔥 [Mobile AddGame] Processing custom FEN:', fenBuilderData.customFEN);
        
        // Store the FEN in state
        setPendingFen(fenBuilderData.customFEN);
        setRootFen(fenBuilderData.customFEN);
        
        // Initialize the game state with the custom FEN
        dispatch({ 
          type: 'INITIALIZE_GAME',
          payload: {
            fen: fenBuilderData.customFEN,
            orientation: fenBuilderData.boardOrientation || 'white',
            moves: [],
            starredMoves: [],
            startPosition: {
              fen: fenBuilderData.customFEN,
              content: '',
              arrows: [],
              highlightedSquares: {},
              variations: []
            }
          }
        });
        
      } else if (fenBuilderData.pgnData && fenBuilderData.comprehensiveMoves) {
        // PGN flow - initialize game with moves from PGN
        // console.log('🔥 [Mobile AddGame] Initializing with PGN moves:', {
        //   movesCount: fenBuilderData.comprehensiveMoves.length,
        //   gameResult: fenBuilderData.gameResult
        // });
        
        dispatch({
          type: 'INITIALIZE_GAME', 
          payload: {
            fen: 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1', // Use 'fen' not 'initialFEN'
            orientation: fenBuilderData.boardOrientation || 'white',
            moves: fenBuilderData.comprehensiveMoves,
            starredMoves: [],
            startPosition: {
              fen: 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
              content: '',
              arrows: [],
              highlightedSquares: {},
              variations: []
            }
          }
        });
      }

      // Auto-apply tags if provided
      if (fenBuilderData.autoTags) {
        const mappedTags = fenBuilderData.autoTags.map(tagValue => {
          const tagConfig = TAGS[tagValue];
          return {
            value: tagValue,
            label: tagConfig?.label || tagValue,
            color: tagConfig?.color || '#333',
            background: tagConfig?.background || '#fff'
          };
        });
        setTags(mappedTags);
        // console.log('🔥 [Mobile AddGame] Applied auto-tags:', mappedTags);
        
        // Also set puzzle mode if Puzzle tag is included
        if (fenBuilderData.autoTags.includes('Puzzle')) {
          setIsPuzzleMode(true);
        }
      }

      // We don't need to clear location state since we're using processedFENBuilderDataRef
      // to prevent re-initialization. Clearing location state causes unwanted re-renders.
    }
  }, [location.state, dispatch, history, onMobileRootFenChange]);

  // Handle pending FEN updates (similar to web version)
  useEffect(() => {
    if (pendingFen) {
      // console.log('🔥 [Mobile AddGame] Processing pendingFen:', pendingFen);
      
      // Clear any existing state
      dispatch({ type: 'SET_MOVES', payload: [] });
      dispatch({ type: 'SET_STARRED_MOVES', payload: [] });
      
      // Update board directly if possible
      if (boardRef.current && boardRef.current.updateBoardFEN) {
        // console.log('🔥 [Mobile AddGame] Updating board via ref for pendingFen');
        boardRef.current.updateBoardFEN(pendingFen);
      }
      
      // Clear pending FEN
      setPendingFen(null);
      
      // Set root FEN for form state
      setRootFen(pendingFen);
    }
  }, [pendingFen, dispatch]);

  // Ensure board restores FEN when switching back to board tab
  useEffect(() => {
    if (activeTab === 'board' && rootFen) {
      // console.log('🔥 [Mobile AddGame] Restoring FEN on board tab switch:', rootFen);
      
      // Try immediate update if board ref is available
      if (boardRef.current && boardRef.current.updateBoardFEN) {
        boardRef.current.updateBoardFEN(rootFen);
      }
      
      // Also try with a delay to handle mounting
      setTimeout(() => {
        if (boardRef.current && boardRef.current.updateBoardFEN) {
          // console.log('🔥 [Mobile AddGame] Delayed board FEN restore:', rootFen);
          boardRef.current.updateBoardFEN(rootFen);
        } else {
          // If board ref still not available, ensure the game state has the correct FEN
          // console.log('🔥 [Mobile AddGame] Board ref not available, ensuring game state has correct FEN');
          dispatch({ 
            type: 'SET_CURRENT_FEN', 
            payload: rootFen 
          });
        }
      }, 100);
    }
  }, [activeTab, rootFen, dispatch]);

  // Fetch user folders
  const fetchUserFolders = useCallback(async () => {
    if (!user?.credentials?.userId) return [];
    
    try {
      const userId = user.credentials.userId;
      
      // Fetch bookmark folders for the current user from main collection
      const folderQuery = query(
        collection(firestore, "bookmarkFolders"),
        where("userId", "==", userId)
      );
      
      const folderSnapshot = await getDocs(folderQuery);
      
      if (!folderSnapshot.empty) {
        // Process folder documents
        const fetchedFolders = folderSnapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().folderName,
          isPrivate: doc.data().isPrivate,
          games: doc.data().games || [],
          folderIndex: doc.data().folderIndex !== undefined ? doc.data().folderIndex : 999
        }));

        // Always include default folders if they don't exist
        let mergedFolders = [...fetchedFolders];

        // Check if Featured folder exists, add default if not
        if (!fetchedFolders.some(folder => folder.name === 'Featured')) {
          mergedFolders.push({
            name: 'Featured',
            isPrivate: false,
            games: [],
            folderIndex: 0
          });
        }

        // Check if Safe folder exists, add default if not  
        if (!fetchedFolders.some(folder => folder.name === 'Safe')) {
          mergedFolders.push({
            name: 'Safe',
            isPrivate: true,
            games: [],
            folderIndex: 0
          });
        }

        // Sort folders by privacy and folderIndex
        mergedFolders.sort((a, b) => {
          if (a.isPrivate !== b.isPrivate) {
            return a.isPrivate ? 1 : -1;
          }
          return a.folderIndex - b.folderIndex;
        });

        setUserFolders(mergedFolders);
        return mergedFolders;
      } else {
        // No folders found, set default folders
        const defaultFolders = [
          { name: 'Featured', isPrivate: false, games: [], folderIndex: 0 },
          { name: 'Safe', isPrivate: true, games: [], folderIndex: 0 }
        ];
        setUserFolders(defaultFolders);
        return defaultFolders;
      }
    } catch (error) {
      console.error('Error fetching user folders:', error);
      return [];
    }
  }, [user]);

  useEffect(() => {
    fetchUserFolders();
  }, [fetchUserFolders]);

  // Debug orientation changes
  useEffect(() => {
    // console.log('Orientation changed to:', orientation);
  }, [orientation]);

  // Helper functions
  const isChallenge = () => tags.some(tag => tag.value === 'Challenge');
  
  const isFormValid = () => {
    const hasTags = tags.length >= 1 && tags.length <= 2;
    const hasValidCaption = gameCaption.trim().length >= 2;
    const hasRequiredMoves = isChallenge() || moves.length > 0;
    return hasTags && hasValidCaption && hasRequiredMoves;
  };

  const handleDifficultyChange = (value) => {
    setSelectedDifficulty(value);
    localStorage.setItem('preferredDifficulty', value);
  };

  // Bookmark notification
  const [bookmarkNotification, setBookmarkNotification] = useState(null);
  
  const showBookmarkNotification = (message, type = 'success') => {
    setBookmarkNotification({ message, type });
    setTimeout(() => setBookmarkNotification(null), 3000);
  };

  // Handle saving game to bookmark folder
  const saveGameToFolder = async (gameId, folderName) => {
    if (folderName === 'NONE') return;
    
    try {
      if (!user?.credentials?.userId) {
        showBookmarkNotification('You must be logged in to bookmark games', 'error');
        return false;
      }
      
      const userId = user.credentials.userId;
      const folder = userFolders.find((f) => f.name === folderName);
      
      if (!folder) {
        showBookmarkNotification(`Folder "${folderName}" not found`, 'error');
        return false;
      }
      
      // Check if folder exists in Firestore
      const folderQuery = query(
        collection(firestore, 'bookmarkFolders'),
        where('userId', '==', userId),
        where('folderName', '==', folderName),
      );
      const folderSnapshot = await getDocs(folderQuery);
      
      if (folderSnapshot.empty) {
        // Create new folder with game using new structure
        const newFolderData = {
          userId: userId,
          folderName: folderName,
          isPrivate: folder.isPrivate || false,
          createdAt: new Date().toISOString(),
          folderIndex: folder.folderIndex || 999,
          games: [{
            gameId: gameId,
            index: 0,
            createdAt: new Date().toISOString()
          }]
        };
        await addDoc(collection(firestore, 'bookmarkFolders'), newFolderData);
        showBookmarkNotification(`Game saved to folder "${folderName}"`, 'success');
      } else {
        // Update existing folder using new structure
        const folderDoc = folderSnapshot.docs[0];
        const folderData = folderDoc.data();
        const games = folderData.games || [];
        
        // Check if the game is already in the folder
        const existingGameIndex = games.findIndex(game => game.gameId === gameId);
        
        if (existingGameIndex !== -1) {
          // Game already exists, move it to index 0 and reindex others
          const updatedGames = [...games];
          const existingGame = updatedGames.splice(existingGameIndex, 1)[0];
          
          // Increment index of all other games
          updatedGames.forEach(game => {
            game.index += 1;
          });
          
          // Add the existing game at index 0
          existingGame.index = 0;
          updatedGames.unshift(existingGame);
          
          await setDoc(folderDoc.ref, {
            ...folderData,
            games: updatedGames,
            updatedAt: new Date().toISOString()
          });
          showBookmarkNotification(`Game moved to top of "${folderName}"`, 'success');
        } else {
          // Game doesn't exist in the folder, add it at index 0
          const updatedGames = [...games];
          
          // Increment index of all existing games
          updatedGames.forEach(game => {
            game.index += 1;
          });
          
          // Add the new game at index 0
          updatedGames.unshift({
            gameId: gameId,
            index: 0,
            createdAt: new Date().toISOString()
          });
          
          await setDoc(folderDoc.ref, {
            ...folderData,
            games: updatedGames,
            updatedAt: new Date().toISOString()
          });
          showBookmarkNotification(`Game saved to folder "${folderName}"`, 'success');
        }
      }
      return true;
    } catch (error) {
      console.error('Error saving bookmark:', error);
      showBookmarkNotification('Failed to save bookmark. Please try again.', 'error');
      return false;
    }
  };

  // Create a new folder and add game to it
  const createNewFolderAndAddGame = async (gameId, folderName, isPrivate) => {
    try {
      if (!user?.credentials?.userId) {
        showBookmarkNotification('You must be logged in to bookmark games', 'error');
        return false;
      }
      
      const userId = user.credentials.userId;
      
      if (!folderName || !folderName.trim()) {
        showBookmarkNotification('Please enter a folder name', 'error');
        return false;
      }
      
      // Check if folder already exists
      const folderQuery = query(
        collection(firestore, 'bookmarkFolders'),
        where('userId', '==', userId),
        where('folderName', '==', folderName.trim()),
      );
      const folderSnapshot = await getDocs(folderQuery);
      
      if (!folderSnapshot.empty) {
        showBookmarkNotification(`Folder "${folderName.trim()}" already exists`, 'error');
        return false;
      }
      
      // Create new folder with game using new structure
      const newFolderData = {
        userId: userId,
        folderName: folderName.trim(),
        isPrivate: isPrivate || false,
        createdAt: new Date().toISOString(),
        folderIndex: 999, // Place new folders at the end
        games: [{
          gameId: gameId,
          index: 0,
          createdAt: new Date().toISOString()
        }]
      };
      
      await addDoc(collection(firestore, 'bookmarkFolders'), newFolderData);
      showBookmarkNotification(`Game saved to new folder "${folderName.trim()}"`, 'success');
      
      // Refresh folders list
      await fetchUserFolders();
      
      return true;
    } catch (error) {
      console.error('Error creating new folder:', error);
      showBookmarkNotification('Failed to create folder. Please try again.', 'error');
      return false;
    }
  };

  // Submit game function
  const onSubmitGame = async () => {
    if (!isFormValid()) return;
    
    const cleanedGameCaption = cleanProfanity(gameCaption);
    
    // Rate limiting check
    const userTier = 'free'; // TODO: Get actual user tier from props/context
    const config = getRateLimit('POST_GAME', userTier);
    const rateKey = buildGlobalRateKey(
      user?.credentials?.userId,
      'postGame',
    );
    const isLimited = checkRateLimit({
      key: rateKey,
      limit: config.limit,
      windowMs: config.windowMs,
    });
    
    if (isLimited) {
      alert('You are posting games too quickly. Please wait a moment and try again.');
      return;
    }
    
    // Validation checks
    if (gameMetadata.gameMode === 'Puzzle' && moves.length === 0) {
      alert('Please add moves for Puzzle mode');
      return;
    }
    
    if (!isChallenge() && moves.length === 0) {
      alert('Please add at least one move');
      return;
    }
    
    const userHandle = user?.credentials?.handle;
    const gameRootFen = state.rootFEN || rootFen || 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
    
    // console.log('🔥 [Mobile AddGame] Submitting game with FEN data:', {
    //   'state.rootFEN': state.rootFEN,
    //   'local rootFen': rootFen,
    //   'final gameRootFen': gameRootFen,
    //   'is default FEN': gameRootFen === 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1'
    // });
    
    try {
      // Create game data object matching web version structure
      const gameData = {
        caption: cleanedGameCaption,
        fen: isChallenge() || !moves.length ? gameRootFen : gameRootFen,
        comments: [],
        suggestedMoves: [],
        moves: moves,
        starredMoves: starredMoves,
        tags: tags.map(tag => tag.value),
        pov: orientation === 'white',
        creatorHandle: userHandle,
        creator: user?.credentials?.userId,
        isChallenge: isChallenge(),
        isPrivate: gameMetadata.isPrivate,
        difficulty: selectedDifficulty,
        // Add player data using correct database structure
        povPlayer:
          playerData.povPlayer[0] || playerData.povPlayer[1]
            ? playerData.povPlayer
            : null,
        oppositePlayer:
          playerData.oppositePlayer[0] || playerData.oppositePlayer[1]
            ? playerData.oppositePlayer
            : null,
        startPosition: (() => {
          try {
            // Use the same pattern as desktop - create start position from FEN
            const startPosData = state.startPosition || createStartPosition(gameRootFen);
            
            // console.log('🔥 [Mobile] Submitting game with startPosition:', {
            //   stateStartPosition: state.startPosition,
            //   createdStartPosition: startPosData,
            //   gameRootFen: gameRootFen,
            //   hasArrows: Array.isArray(startPosData.arrows) && startPosData.arrows.length > 0,
            //   hasHighlights: startPosData.highlightedSquares && Object.keys(startPosData.highlightedSquares).length > 0
            // });
            
            // Return with proper structure matching database expectations
            return {
              fen: startPosData.fen || gameRootFen,
              content: startPosData.content || '',
              arrows: startPosData.arrows || [],
              highlightedSquares: startPosData.highlightedSquares || {},
              variations: startPosData.variations || []
            };
          } catch (error) {
            console.error('🔥 [Mobile] Error creating startPosition:', error);
            // Fallback to basic structure
            return {
              fen: gameRootFen,
              content: '',
              arrows: [],
              highlightedSquares: {},
              variations: []
            };
          }
        })(),
        visibility: gameMetadata.visibility,
        bookmarkData: selectedBookmarkFolder !== 'NONE' ? {
          folderName: selectedBookmarkFolder,
          isNewFolder: isCreatingNewFolder,
          newFolderName: newFolderName,
          newFolderPrivate: newFolderPrivate
        } : null
      };
      
      // console.log('🔥 [Mobile] Complete gameData being sent to API:', gameData);
      
      // Call postGame API
      const response = await postGame(gameData);
      
      if (response?.data?.id) {
        // Handle bookmark if needed
        if (selectedBookmarkFolder !== 'NONE' && !isCreatingNewFolder) {
          try {
            await saveGameToFolder(response.data.id, selectedBookmarkFolder);
          } catch (bookmarkError) {
            console.error('Error saving bookmark:', bookmarkError);
          }
        } else if (isCreatingNewFolder && newFolderName) {
          try {
            await createNewFolderAndAddGame(response.data.id, newFolderName, newFolderPrivate);
          } catch (bookmarkError) {
            console.error('Error creating folder:', bookmarkError);
          }
        }
        
        // Navigate to the game view
        history.push(`/games/${response.data.id}`, { from: '/addgame' });
      }
    } catch (error) {
      console.error('Error submitting game:', error);
      if (error.response?.data?.error) {
        alert(error.response.data.error);
      } else {
        alert('Failed to publish game. Please try again.');
      }
    }
  };

  // Simplified update functions
  const updateContentForMove = (content) => {
    setMoveContent(content);
  };

  const updateVariationContent = (content) => {
    setVariationContent(content);
  };

  // Callback for comment move content changes - handles both main line and variation annotations
  const handleCommentMoveContentChange = (moveIndex, content) => {
    // This callback is used by MobileMovesEngine for annotation saving
    // The actual save logic is implemented in MobileMoves.jsx component
    // console.log('🔥 [AddGame] Comment move content change:', { moveIndex, content });
  };

  const setSkipMoveIndexUpdate = () => {
    // Placeholder for now
  };

  const isMoveInVariation = () => {
    // Placeholder for now
    return false;
  };

  const handleFenChange = () => {
    // Placeholder for now
  };

  const handlePGNSubmit = (pgnData) => {
    // console.log('🔥 [Mobile AddGame] PGN submitted:', {
    //   movesCount: pgnData.comprehensiveMoves?.length,
    //   gameResult: pgnData.gameResult
    // });

    // Initialize game with moves from PGN (similar to FENBuilder flow)
    dispatch({
      type: 'INITIALIZE_GAME', 
      payload: {
        fen: 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
        orientation: 'white',
        moves: pgnData.comprehensiveMoves || [],
        starredMoves: [],
        startPosition: {
          fen: 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
          content: '',
          arrows: [],
          highlightedSquares: {},
          variations: []
        }
      }
    });

    // Set the current move to the last move if moves exist
    if (pgnData.comprehensiveMoves && pgnData.comprehensiveMoves.length > 0) {
      setTimeout(() => {
        // Ensure the move index is a string (required by the game state system)
        const lastMoveIndex = (pgnData.comprehensiveMoves.length - 1).toString();
        // console.log('[Mobile AddGame] Setting current move to:', lastMoveIndex);
        dispatch({
          type: 'SET_CURRENT_MOVE',
          payload: lastMoveIndex
        });
      }, 100);
    }
  };

  const handlePlayerDataChange = (newPlayerData) => {
    setPlayerData(newPlayerData);
  };

  // Board Setup navigation protection handlers
  const handleBoardSetupClick = () => {
    if (hasUnsavedData) {
      setBoardSetupPuzzleMode(false);
      setShowBoardSetupConfirm(true);
    } else {
      history.push('/mobile/fen-builder');
    }
  };

  // Create a Puzzle navigation protection handler  
  const handleCreatePuzzleClick = () => {
    if (hasUnsavedData) {
      setBoardSetupPuzzleMode(true);
      setShowBoardSetupConfirm(true);
    } else {
      // Execute the original Create a Puzzle logic
      setGameMetadata({ ...gameMetadata, gameMode: 'Puzzle' });
      setTags([{ 
        value: 'Puzzle', 
        label: TAGS.Puzzle.label,
        color: TAGS.Puzzle.color,
        background: TAGS.Puzzle.background 
      }]);
      setIsPuzzleMode(true);
      history.push('/mobile/fen-builder', { 
        puzzleMode: true,
        gameMode: 'Puzzle' 
      });
    }
  };

  const handleBoardSetupConfirm = () => {
    setShowBoardSetupConfirm(false);
    if (boardSetupPuzzleMode) {
      // Execute Create a Puzzle logic
      setGameMetadata({ ...gameMetadata, gameMode: 'Puzzle' });
      setTags([{ 
        value: 'Puzzle', 
        label: TAGS.Puzzle.label,
        color: TAGS.Puzzle.color,
        background: TAGS.Puzzle.background 
      }]);
      setIsPuzzleMode(true);
      history.push('/mobile/fen-builder', { 
        puzzleMode: true,
        gameMode: 'Puzzle' 
      });
    } else {
      // Regular board setup
      history.push('/mobile/fen-builder');
    }
  };

  const handleBoardSetupCancel = () => {
    setShowBoardSetupConfirm(false);
    setBoardSetupPuzzleMode(false);
  };


  // Helper functions to map between UI display and database structure
  const getWhitePlayerData = () => {
    // If orientation is white, povPlayer is white; if black, oppositePlayer is white
    return orientation === 'white' ? playerData.povPlayer : playerData.oppositePlayer;
  };

  const getBlackPlayerData = () => {
    // If orientation is white, oppositePlayer is black; if black, povPlayer is black
    return orientation === 'white' ? playerData.oppositePlayer : playerData.povPlayer;
  };

  const setWhitePlayerData = (name, rating) => {
    const newPlayerData = { ...playerData };
    if (orientation === 'white') {
      newPlayerData.povPlayer = [name, rating];
    } else {
      newPlayerData.oppositePlayer = [name, rating];
    }
    setPlayerData(newPlayerData);
  };

  const setBlackPlayerData = (name, rating) => {
    const newPlayerData = { ...playerData };
    if (orientation === 'white') {
      newPlayerData.oppositePlayer = [name, rating];
    } else {
      newPlayerData.povPlayer = [name, rating];
    }
    setPlayerData(newPlayerData);
  };

  // Control panel toggle handlers (matching web version)
  const handleStockfishToggle = () => {
    const newValue = !localShowStockfish;
    setLocalShowStockfish(newValue);
    localStorage.setItem('showStockfish', newValue.toString());
  };

  const handleStockfishEvalToggle = () => {
    const newValue = !localShowStockfishEval;
    setLocalShowStockfishEval(newValue);
    localStorage.setItem('showStockfishEval', newValue.toString());
  };

  // Handle annotation popup open
  const handleAnnotationClick = () => {
    const currentMoveId = state.currentMove;
    let currentContent = '';
    
    // Get current move's content
    if (currentMoveId === '-1') {
      currentContent = state.startPosition?.content || '';
    } else if (!currentMoveId.includes('|')) {
      const moveIndex = parseInt(currentMoveId, 10);
      currentContent = state.moves[moveIndex]?.content || '';
    }
    
    setPopupContent(currentContent);
    setShowAnnotationPopup(true);
    setToolMode('TEXT');
  };

  // Handle annotation save
  const handleAnnotationSave = () => {
    const currentMoveId = state.currentMove;
    
    if (currentMoveId === '-1') {
      // Update start position content
      dispatch({
        type: 'UPDATE_START_POSITION',
        payload: {
          ...state.startPosition,
          content: popupContent
        }
      });
    } else if (!currentMoveId.includes('|')) {
      // Update move content
      dispatch({
        type: 'UPDATE_MOVE_PROPERTIES',
        payload: {
          moveId: currentMoveId,
          properties: {
            content: popupContent
          }
        }
      });
    }
    
    // Update local moveContent state for compatibility
    setMoveContent(popupContent);
    updateContentForMove(popupContent);
    
    setShowAnnotationPopup(false);
    setPopupContent('');
    setToolMode('NORMAL');
  };

  // Handle annotation cancel
  const handleAnnotationCancel = () => {
    setShowAnnotationPopup(false);
    setPopupContent('');
    setToolMode('NORMAL');
  };

  // Handle undo move - uses the same logic as web version
  const handleUndoMove = () => {
    if (!state.moves || state.moves.length === 0 || state.currentMove === '-1') {
      return false;
    }

    // Dispatch custom event before calling undoMove (matching web version)
    window.dispatchEvent(new Event('chess-move-undo'));
    
    // Call undoMove directly - it handles both last move undo and truncation modal internally
    const result = undoMove();
    
    // If undoMove returns false, it means we're not at the last move
    // Enable undo mode to show END LINE HERE and X buttons in icon bar
    if (!result) {
      setUndoMode(true);
    }
  };

  // Handle truncation confirmation
  const handleTruncateConfirm = () => {
    // Use undoMove with forceDelete=true to truncate from current position
    undoMove(true);
    setUndoMode(false); // Exit undo mode
  };

  // Handle undo cancel
  const handleUndoCancel = () => {
    setUndoMode(false); // Exit undo mode without doing anything
  };

  // Handle delete game request
  const handleDeleteGame = () => {
    setDeleteMode(true); // Enter delete mode
  };

  // Handle delete game confirmation
  const handleDeleteConfirm = () => {
    // Reset the entire game state
    dispatch({ type: 'RESET_GAME' });
    setDeleteMode(false); // Exit delete mode
  };

  // Handle delete cancel
  const handleDeleteCancel = () => {
    setDeleteMode(false); // Exit delete mode without doing anything
  };


  // Helper function to check if game is in puzzle mode
  const isPuzzle = () => {
    return isPuzzleMode || 
           gameMetadata.gameMode === 'Puzzle' || 
           tags.some(tag => tag.value === 'Puzzle');
  };

  // Expose puzzle mode globally for variation prevention
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.isPuzzleMode = isPuzzle();
    }
  }, [isPuzzleMode, gameMetadata.gameMode, tags]);

  // Helper function to check if game is in lesson mode
  const isLesson = () => {
    return gameMetadata.gameMode === 'Lesson' || 
           tags.some(tag => tag.value === 'Lesson');
  };

  // Helper function to check if current move has content (including variations and nested variations)
  const getCurrentMoveHasContent = () => {
    const currentMoveId = state.currentMove;
    
    if (currentMoveId === '-1') {
      // Start position
      const hasContent = state.startPosition?.content && state.startPosition.content.length > 0;
      return hasContent;
    } else if (!currentMoveId.includes('|')) {
      // Main line move
      const moveIndex = parseInt(currentMoveId, 10);
      const hasContent = state.moves[moveIndex]?.content && state.moves[moveIndex].content.length > 0;
      return hasContent;
    } else {
      // Variation move (including nested variations) - use VariationPathUtils
      try {
        const { getNodeAtPathInfinite } = require('../../util/VariationPathUtils');
        const result = getNodeAtPathInfinite(state.moves, currentMoveId, state);
        
        if (!result.success) {
          console.warn('Failed to navigate to variation path:', currentMoveId, result.error);
          return false;
        }
        
        const hasContent = result.node?.content && result.node.content.length > 0;
        
        // Debug logging for variation content detection
        if (currentMoveId.includes('|')) {
          // console.log('Variation content check:', {
          //   path: currentMoveId,
          //   hasContent,
          //   content: result.node?.content,
          //   nodeExists: !!result.node
          // });
        }
        
        return hasContent;
      } catch (error) {
        console.error('Error checking variation content for path:', currentMoveId, error);
        return false;
      }
    }
  };

  // Helper functions to get current move annotations
  const getCurrentMoveHighlights = () => {
    const currentMoveId = state.currentMove;
    if (currentMoveId === '-1') return state.startPosition.highlightedSquares || {};
    
    // For main line moves
    if (!currentMoveId.includes('|')) {
      const moveIndex = parseInt(currentMoveId, 10);
      return state.moves[moveIndex]?.highlightedSquares || {};
    }
    
    // For variation moves - would need path traversal
    // For now, return empty object for variations
    return {};
  };

  const getCurrentMoveArrows = () => {
    const currentMoveId = state.currentMove;
    if (currentMoveId === '-1') return state.startPosition.arrows || [];
    
    // For main line moves
    if (!currentMoveId.includes('|')) {
      const moveIndex = parseInt(currentMoveId, 10);
      return state.moves[moveIndex]?.arrows || [];
    }
    
    // For variation moves - would need path traversal
    // For now, return empty array for variations
    return [];
  };

  return (
    <div style={{
      width: '100vw',
      height: 'calc(100dvh - 55px)', // Account for bottom navigation
    
      backgroundColor: '#1a1a1a',
      // background: 'blue',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      // margin: 0,
      // padding: 0,
      padding: 'max(env(safe-area-inset-top), 0px) 0 max(env(safe-area-inset-bottom), 0px) 0'
      // position: 'fixed',
      // top: 0,
      // left: 0,
    }}>
      {/* Modals */}
      {openModalType === 'GameModeChooser' && (
        <GameModeChooserModal
          onClose={() => setOpenModalType('')}
          onGameModeUpdate={(mode) => {
            setGameMetadata({ ...gameMetadata, gameMode: mode });
            if (mode === 'Puzzle') {
              setTags([{ 
                value: 'Puzzle', 
                label: TAGS.Puzzle.label,
                color: TAGS.Puzzle.color,
                background: TAGS.Puzzle.background 
              }]);
              setIsPuzzleMode(true);
            } else if (mode === 'Challenge') {
              setTags([{ 
                value: 'Challenge', 
                label: TAGS.Challenge.label,
                color: TAGS.Challenge.color,
                background: TAGS.Challenge.background 
              }]);
            }
            setOpenModalType('');
          }}
        />
      )}

      {/* Mobile PGN Upload Modal */}
    
      <MobilePGNUploadModal
        isOpen={showPGNUploadModal}
        onClose={() => setShowPGNUploadModal(false)}
        onPGNSubmit={handlePGNSubmit}
      />

      {/* Board Setup Navigation Confirmation Modal */}
      <NavigationConfirmModal
        show={showBoardSetupConfirm}
        onContinue={handleBoardSetupConfirm}
        onCancel={handleBoardSetupCancel}
      />

      {/* Mobile FAQ Modal */}
      {showFAQModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          zIndex: 10000,
          display: 'flex',
          flexDirection: 'column',
          paddingTop: isNativeApp() ? '60px' : '0px' // Account for native app header
        }}>
          {/* Mobile FAQ Header */}
          <div style={{
            backgroundColor: '#1a1a1a',
            padding: '10px',
            borderBottom: '1px solid #333',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <h3 style={{ color: 'gold', margin: 0, fontSize: '18px' }}>
              Content Creation Guide
            </h3>
            <button
              onClick={() => setShowFAQModal(false)}
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                color: 'white',
                fontSize: '24px',
                cursor: 'pointer',
                padding: '5px'
              }}
            >
              <i className="fa-solid fa-times"></i>
            </button>
          </div>
          
          {/* Mobile FAQ Content */}
          <div style={{
            flex: 1,
            overflow: 'auto',
            WebkitOverflowScrolling: 'touch',
            padding: '15px 15px 80px 15px', // Extra bottom padding for better scrolling
            backgroundColor: '#000'
          }}>
            {AddGameFAQData.map((faq, index) => {
              const isVideoFAQ = faq.type === 'video';
              
              return (
                <div
                  key={index}
                  style={{
                    marginBottom: '15px',
                    backgroundColor: '#1a1a1a',
                    borderRadius: '8px',
                    border: isVideoFAQ ? '2px solid #58b047' : '1px solid #333',
                    overflow: 'hidden',
                    ...(isVideoFAQ && {
                      boxShadow: '0 0 10px rgba(88, 176, 71, 0.3)'
                    })
                  }}
                >
                  <div
                    style={{
                      padding: '15px',
                      borderBottom: '1px solid #333',
                      backgroundColor: isVideoFAQ ? '#2a1a1a' : '#2a2a2a',
                      cursor: isVideoFAQ ? 'pointer' : 'default'
                    }}
                    onClick={isVideoFAQ ? () => setExpandedVideoFAQ(!expandedVideoFAQ) : undefined}
                  >
                    <h4 style={{ 
                      color: isVideoFAQ ? '#58b047' : 'gold', 
                      margin: 0, 
                      fontSize: '16px',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      <span style={{ 
                        backgroundColor: isVideoFAQ ? '#58b047' : 'gold', 
                        color: isVideoFAQ ? 'white' : 'black', 
                        borderRadius: '50%', 
                        width: '24px', 
                        height: '24px', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        marginRight: '12px',
                        fontSize: '14px',
                        fontWeight: 'bold'
                      }}>
                        {isVideoFAQ ? <i className="fa-solid fa-video"></i> : index}
                      </span>
                      {faq.question}
                      {isVideoFAQ && (
                        <i 
                          className={`fa-solid fa-chevron-${expandedVideoFAQ ? 'up' : 'down'}`}
                          style={{ marginLeft: 'auto', fontSize: '14px' }}
                        />
                      )}
                    </h4>
                  </div>
                  
                  {/* Regular FAQ Content */}
                  {!isVideoFAQ && (
                    <div style={{ padding: '15px' }}>
                      {Array.isArray(faq.answer) ? (
                        <ul style={{ margin: 0, paddingLeft: '20px', color: 'white' }}>
                          {faq.answer.map((point, pointIndex) => (
                            <li key={pointIndex} style={{ marginBottom: '8px', lineHeight: '1.4' }}>
                              <span style={{ fontSize: '14px' }}>
                                {point}
                              </span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p style={{ 
                          color: 'white', 
                          margin: 0, 
                          fontSize: '14px',
                          lineHeight: '1.5'
                        }}>
                          {faq.answer}
                        </p>
                      )}
                    </div>
                  )}
                  
                  {/* Video FAQ Content - Expandable */}
                  {isVideoFAQ && expandedVideoFAQ && (
                    <div style={{ padding: '15px' }}>
                      {faq.videos.map((video, videoIndex) => (
                        <div key={videoIndex} style={{ marginBottom: '20px' }}>
                          <h5 style={{ 
                            color: 'gold', 
                            fontSize: '18px', 
                            fontWeight: 'bold',
                            margin: '0 0 10px 0'
                          }}>
                            <i className="fa-solid fa-play" style={{ fontSize: '20px', marginRight: '8px' }}></i> {video.title}
                          </h5>
                          <div style={{ 
                            position: 'relative', 
                            paddingBottom: '177.78%', /* 9:16 vertical aspect ratio */
                            height: 0,
                            borderRadius: '8px',
                            overflow: 'hidden'
                          }}>
                            <iframe
                              src={`https://www.youtube-nocookie.com/embed/${video.youtubeId}?playsinline=1&modestbranding=1&rel=0&enablejsapi=1&origin=${encodeURIComponent(window.location.origin)}`}
                              title={video.title}
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                              allowFullScreen={true}
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                border: 'none'
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            
            {/* Spacer to ensure last item is fully visible */}
            <div style={{ height: '20px' }} />
          </div>
        </div>
      )}


      {/* Board Column - Fixed Width Container */}
      <div style={{ 
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        // padding: '8px',
        flex: 1,
        overflow: 'hidden',
        // paddingTop: 'clamp(2px, 1vh, 10px)', // Smaller dynamic padding for Analyze/Share tabs
        // paddingBottom: 'clamp(2px, 1vh, 10px)' // Smaller dynamic padding for bottom
        paddingBottom: '45px'
      }}>
        <div style={{
          width: `${boardSize}px`,
          maxWidth: 'calc(100vw - 16px)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          
          gap: 'clamp(4px, 1vh, 8px)',
        }}>

          {/* SetupDefaults */}
          <SetupDefaults />
            
          {/* Icon Row Above Board - Annotation Tools */}
          <div style={{
            display: 'flex',
            justifyContent: undoMode || toolMode === 'DELETE' ? 'center' : 'center',
            alignItems: 'center',
            
            width: '65%',
            padding: '4px 0px',
            backgroundColor: '#0a0a0a',
            border: '1px solid #444',
            borderRadius: '10px',
            height: '40px',
            gap: undoMode || toolMode === 'DELETE' ? '0' : '5px'
          }}>
              {undoMode ? (
                // Undo Mode - Show END LINE HERE and X buttons
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <button
                    onClick={handleTruncateConfirm}
                    style={{
                      backgroundColor: '#ff0050',
                      border: '2px solid #ff0050',
                      color: 'white',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      padding: '6px 12px',
                      borderRadius: '4px',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.backgroundColor = '#ff3366';
                      e.target.style.borderColor = '#ff3366';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.backgroundColor = '#ff0050';
                      e.target.style.borderColor = '#ff0050';
                    }}
                  >
                    END LINE HERE
                  </button>
                  
                  <button
                    onClick={handleUndoCancel}
                    style={{
                      backgroundColor: 'transparent',
                      border: '2px solid #888',
                      color: '#888',
                      fontSize: '16px',
                      cursor: 'pointer',
                      padding: '6px 10px',
                      borderRadius: '4px',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.color = '#ccc';
                      e.target.style.borderColor = '#ccc';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.color = '#888';
                      e.target.style.borderColor = '#888';
                    }}
                    title="Cancel undo"
                  >
                    <i className="fa-solid fa-times"></i>
                  </button>
                </div>
              ) : toolMode === 'NORMAL' ? (
                // Normal Mode - Show All Icons
                <>
                  

                  {/* Delete */}
                  <button
                    onClick={() => {
                      if (state.moves.length === 0 && !isPuzzle() && !isLesson()) return;
                      setToolMode('DELETE');
                      setArrowStartSquare(null); // Clear any pending arrow
                    }}
                    disabled={state.moves.length === 0 && !isPuzzle() && !isLesson()}
                    style={{
                      backgroundColor: 'transparent',
                      border: 'none',
                      color: (state.moves.length === 0 && !isPuzzle() && !isLesson()) ? '#444' : '#888',
                      fontSize: '20px',
                      cursor: (state.moves.length === 0 && !isPuzzle() && !isLesson()) ? 'default' : 'pointer',
                      padding: '8px',
                      borderRadius: '4px',
                      transition: 'color 0.2s',
                      opacity: (state.moves.length === 0 && !isPuzzle() && !isLesson()) ? '0.3' : '1'
                    }}
                    onMouseOver={(e) => {
                      if (state.moves.length > 0 || isPuzzle() || isLesson()) {
                        e.target.style.color = '#ff0050';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (state.moves.length > 0 || isPuzzle() || isLesson()) {
                        e.target.style.color = '#888';
                      }
                    }}
                    title={(state.moves.length === 0 && !isPuzzle() && !isLesson()) ? 'No moves to delete' : 'Delete game'}
                  >
                    <i className="fa-solid fa-trash"></i>
                  </button>

                  {/* FAQ */}
                  <button
                    onClick={() => setShowFAQModal(true)}
                    style={{
                      backgroundColor: 'transparent',
                      border: 'none',
                      color: state.moves.length === 0 ? '#4CAF50' : '#888',
                      fontSize: '20px',
                      cursor: 'pointer',
                      padding: '8px',
                      borderRadius: '4px',
                      transition: 'color 0.2s'
                    }}
                    onMouseOver={(e) => e.target.style.color = 'white'}
                    onMouseOut={(e) => e.target.style.color = state.moves.length === 0 ? '#4CAF50' : '#888'}
                  >
                    <i className="fa-solid fa-question"></i>
                  </button>

                  

                  {/* Upload - FEN Builder */}
                  <button
                    onClick={handleBoardSetupClick}
                    style={{
                      backgroundColor: 'transparent',
                      border: 'none',
                      color: '#888',
                      fontSize: '20px',
                      cursor: 'pointer',
                      padding: '8px',
                      borderRadius: '4px',
                      transition: 'color 0.2s'
                    }}
                    onMouseOver={(e) => e.target.style.color = 'white'}
                    onMouseOut={(e) => e.target.style.color = '#888'}
                  >
                    <i className="fa-solid fa-chess-board"></i>
                  </button>

                  {/* Flip Board */}
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const newOrientation = orientation === 'white' ? 'black' : 'white';
                      // console.log('Current orientation:', orientation, '-> New orientation:', newOrientation);
                      
                      // Update GameStateContext orientation
                      dispatch({ type: 'SET_ORIENTATION', payload: newOrientation });
                    }}
                    style={{
                      backgroundColor: 'transparent',
                      border: 'none',
                      color: '#888',
                      fontSize: '20px',
                      cursor: 'pointer',
                      padding: '8px',
                      borderRadius: '4px',
                      transition: 'color 0.2s'
                    }}
                    onMouseOver={(e) => e.target.style.color = 'white'}
                    onMouseOut={(e) => e.target.style.color = '#888'}
                  >
                    <i className="fa-solid fa-retweet"></i>
                  </button>

                  {/* Undo Move */}
                  <button
                    onClick={handleUndoMove}
                    disabled={state.moves.length === 0 || state.currentMove === '-1'}
                    style={{
                      backgroundColor: 'transparent',
                      border: 'none',
                      color: (state.moves.length === 0 || state.currentMove === '-1') ? '#444' : '#888',
                      fontSize: '20px',
                      cursor: (state.moves.length === 0 || state.currentMove === '-1') ? 'default' : 'pointer',
                      padding: '8px',
                      borderRadius: '4px',
                      transition: 'color 0.2s',
                      opacity: (state.moves.length === 0 || state.currentMove === '-1') ? '0.3' : '1'
                    }}
                    onMouseOver={(e) => {
                      if (state.moves.length > 0 && state.currentMove !== '-1') {
                        e.target.style.color = '#ccc';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (state.moves.length > 0 && state.currentMove !== '-1') {
                        e.target.style.color = '#888';
                      }
                    }}
                    title={state.moves.length === 0 || state.currentMove === '-1' ? 'No moves to undo' : 'Undo move'}
                  >
                    <i className="fa-solid fa-scissors fa-rotate-270"></i>
                  </button>

                  

                </>
              ) : toolMode === 'DELETE' ? (
                // Delete Mode - Match undo pattern styling
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <button
                    onClick={() => {
                      if (isPuzzle() || isLesson()) {
                        // For puzzles and lessons, completely reset to standard starting position
                        dispatch({ 
                          type: 'INITIALIZE_GAME', 
                          payload: {
                            fen: 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
                            orientation: 'white',
                            moves: [],
                            starredMoves: [],
                            startPosition: {
                              fen: 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
                              content: '',
                              arrows: [],
                              highlightedSquares: {},
                              variations: []
                            }
                          }
                        });
                        
                        // Clear puzzle/lesson mode and metadata
                        setIsPuzzleMode(false);
                        
                        // Reset to standard starting position - clear all custom FEN references
                        setRootFen('rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1');
                        
                        // Clear FEN builder data and processed flag to prevent re-initialization
                        processedFENBuilderDataRef.current = false;
                        
                        // Clear any pending FEN updates
                        setPendingFen(null);
                        
                        // Reset board orientation to default
                        dispatch({ type: 'SET_ORIENTATION', payload: 'white' });
                        
                        // Force board to update to starting position
                        if (boardRef.current && boardRef.current.updateBoardFEN) {
                          boardRef.current.updateBoardFEN('rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1');
                        }
                        
                        // Clear global window FEN reference
                        if (typeof window !== 'undefined') {
                          window.currentFEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
                        }
                        
                        // Notify root FEN change
                        if (onMobileRootFenChange) {
                          onMobileRootFenChange('rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1');
                        }
                        
                        // Clear location state to prevent re-processing FEN builder data
                        // Note: We're already on /mobile/add, just clearing the state
                        window.history.replaceState(null, '', window.location.pathname);
                        
                        // console.log('🔥 [Mobile AddGame] Puzzle deleted - reset to starting position');
                      } else {
                        // For regular games, just clear moves
                        dispatch({ 
                          type: 'SET_MOVES', 
                          payload: [] // SET_MOVES expects the moves array directly
                        });
                        
                        // Reset current move separately
                        dispatch({
                          type: 'SET_CURRENT_MOVE',
                          payload: '-1'
                        });
                      }
                      
                      // Reset all local form states (common for both puzzles and regular games)
                      setGameCaption('');
                      setTags([]);
                      setSelectedDifficulty('All Levels');
                      setGameMetadata({
                        visibility: 'Public',
                        isPrivate: false,
                        gameMode: undefined
                      });
                      setPlayerData({
                        povPlayer: ['', ''],
                        oppositePlayer: ['', ''],
                      });
                      setSelectedBookmarkFolder('NONE');
                      setIsCreatingNewFolder(false);
                      setNewFolderName('');
                      setShareStep(1);
                      
                      // Reset annotation states
                      setToolMode('NORMAL');
                      setSelectedColor(constants.CHESSBOARD.COLOR_VARIANTS.RED);
                      setArrowStartSquare(null);
                      
                      // Reset board orientation to default
                      dispatch({ type: 'SET_ORIENTATION', payload: 'white' });
                      
                      // console.log('Game data cleared successfully');
                    }}
                    style={{
                      backgroundColor: '#ff0050',
                      border: '2px solid #ff0050',
                      color: 'white',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      padding: '6px 12px',
                      borderRadius: '4px',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.backgroundColor = '#ff3366';
                      e.target.style.borderColor = '#ff3366';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.backgroundColor = '#ff0050';
                      e.target.style.borderColor = '#ff0050';
                    }}
                  >
                    DELETE GAME
                  </button>
                  
                  <button
                    onClick={() => setToolMode('NORMAL')}
                    style={{
                      backgroundColor: 'transparent',
                      border: '2px solid #888',
                      color: '#888',
                      fontSize: '16px',
                      cursor: 'pointer',
                      padding: '6px 10px',
                      borderRadius: '4px',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.color = '#ccc';
                      e.target.style.borderColor = '#ccc';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.color = '#888';
                      e.target.style.borderColor = '#888';
                    }}
                    title="Cancel delete"
                  >
                    <i className="fa-solid fa-times"></i>
                  </button>
                </div>
              ) : (toolMode === 'SQUARE' || toolMode === 'ARROW') ? (
                // Square/Arrow Mode with Color Picker
                <>
                  <button
                    onClick={() => {
                      setToolMode('NORMAL');
                      setArrowStartSquare(null); // Clear any pending arrow
                    }}
                    style={{
                      backgroundColor: 'transparent',
                      border: 'none',
                      color: '#888',
                      fontSize: '16px',
                      cursor: 'pointer',
                      padding: '4px'
                    }}
                  >
                    <i className="fa-solid fa-times"></i>
                  </button>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
                    <span style={{ color: 'white', fontSize: '16px' }}>
                      {toolMode === 'SQUARE' ? 'Square' : 'Arrow'}
                      {toolMode === 'ARROW' && arrowStartSquare && (
                        <span style={{ color: '#888', fontSize: '14px', marginLeft: '8px' }}>
                          from {arrowStartSquare}
                        </span>
                      )}
                    </span>
                    
                    {/* Color Picker */}
                    <div style={{ display: 'flex', gap: '6px' }}>
                      {Object.entries(constants.CHESSBOARD.COLOR_VARIANTS).map(([name, color]) => (
                        <button
                          key={name}
                          onClick={() => setSelectedColor(color)}
                          style={{
                            backgroundColor: color,
                            border: selectedColor === color ? '2px solid white' : '1px solid #666',
                            borderRadius: '50%',
                            width: '24px',
                            height: '24px',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                        />
                      ))}
                    </div>
                  </div>
                  
                  {/* Clear Button */}
                  <button
                    onClick={() => {
                      const currentMoveId = state.currentMove;
                      
                      if (toolMode === 'SQUARE') {
                        // Clear all highlighted squares
                        if (currentMoveId === '-1') {
                          // Start position
                          dispatch({
                            type: 'UPDATE_START_POSITION_VISUALS',
                            payload: {
                              highlightedSquares: {},
                              arrows: state.startPosition.arrows || []
                            }
                          });
                        } else if (!currentMoveId.includes('|')) {
                          // Main line move
                          dispatch({
                            type: 'UPDATE_MOVE_PROPERTIES',
                            payload: {
                              moveId: currentMoveId,
                              properties: {
                                highlightedSquares: {}
                              }
                            }
                          });
                        } else {
                          // Variation move - use path-based action
                          dispatch({
                            type: 'UPDATE_MOVE_PROPERTIES_AT_PATH',
                            payload: {
                              path: currentMoveId,
                              properties: {
                                highlightedSquares: {}
                              }
                            }
                          });
                        }
                      } else if (toolMode === 'ARROW') {
                        // Clear all arrows
                        if (currentMoveId === '-1') {
                          // Start position
                          dispatch({
                            type: 'UPDATE_START_POSITION_VISUALS',
                            payload: {
                              arrows: [],
                              highlightedSquares: state.startPosition.highlightedSquares || {}
                            }
                          });
                        } else if (!currentMoveId.includes('|')) {
                          // Main line move
                          dispatch({
                            type: 'UPDATE_MOVE_PROPERTIES',
                            payload: {
                              moveId: currentMoveId,
                              properties: {
                                arrows: []
                              }
                            }
                          });
                        } else {
                          // Variation move - use path-based action
                          dispatch({
                            type: 'UPDATE_MOVE_PROPERTIES_AT_PATH',
                            payload: {
                              path: currentMoveId,
                              properties: {
                                arrows: []
                              }
                            }
                          });
                        }
                        // Also clear any pending arrow start
                        setArrowStartSquare(null);
                      }
                      
                      // Force immediate board visual clearing
                      if (toolMode === 'SQUARE' && boardRef.current && boardRef.current.clearAllSquares) {
                        // console.log('🔄 [AddGame] Directly clearing squares on board for move:', currentMoveId);
                        boardRef.current.clearAllSquares();
                      } else if (toolMode === 'ARROW' && boardRef.current && boardRef.current.clearAllArrows) {
                        // console.log('🔄 [AddGame] Directly clearing arrows on board for move:', currentMoveId);
                        boardRef.current.clearAllArrows();
                      }
                      
                      // Secondary: also reload visuals after a short delay to ensure state consistency
                      setTimeout(() => {
                        if (boardRef.current && boardRef.current.loadAllVisuals) {
                          // console.log('🔄 [AddGame] Reloading visuals after clear for move:', currentMoveId);
                          boardRef.current.loadAllVisuals(currentMoveId);
                        }
                      }, 100);
                    }}
                    style={{
                      backgroundColor: '#ff0050',
                      border: 'none',
                      color: 'white',
                      fontSize: '12px',
                      cursor: 'pointer',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      marginLeft: '8px',
                      fontWeight: 'bold'
                    }}
                    title={`Clear all ${toolMode === 'SQUARE' ? 'highlighted squares' : 'arrows'} for current move`}
                  >
                    Clear
                  </button>
                </>
              ) : null}
          </div>
          
          {/* Chess Board */}
          <div style={{ width: '', display: 'flex', justifyContent: 'center', alignItems: 'center',
          }}>
            <ChessAnalysisWrapper>
              <BoardThree
                    ref={boardRef}
                    boardSize={boardSize}
                    boardOrientation={orientation}
                    boardTheme={localStorage.getItem('boardTheme') || 'blue'}
                    showBoardNotation={true}
                    isViewMode={false}
                    showCollapsedIcons={false}
                    onPlayerDataChange={handlePlayerDataChange}
                    onFenChange={handleFenChange}
                    // Pass active color for mobile diagram modes (prioritize mobile tools over desktop)
                    activeColor={mobileActiveColor !== undefined ? mobileActiveColor : (toolMode === 'SQUARE' ? selectedColor : undefined)}
                    // Mobile arrow mode support (prioritize mobile tools over desktop)
                    mobileArrowMode={mobileArrowMode || toolMode === 'ARROW'}
                    mobileArrowColor={mobileArrowColor !== undefined ? mobileArrowColor : (toolMode === 'ARROW' ? selectedColor : undefined)}
                    mobileArrowStart={mobileArrowStart || arrowStartSquare}
                    onMobileArrowStart={(startSquare) => {
                      setArrowStartSquare(startSquare);
                      setMobileArrowStart(startSquare);
                    }}
                    // Keep dragging enabled for normal mode only
                    arePiecesDraggable={toolMode === 'NORMAL' && !mobileArrowMode && mobileActiveColor === undefined}
                  />
            </ChessAnalysisWrapper>
          </div>
          
          {/* Navigation Controls - Hide on iPads */}
          {!isTablet && (moves.length > 0 || isPuzzle() || isLesson() || isPuzzleMode || tags.some(tag => tag.value === 'Puzzle')) && (
            <div style={{
              border: '1px solid #444',
              borderRadius: '10px',
              padding: '0px 4px',
              backgroundColor: '#0a0a0a',
              width: '100%'
            }}>
              <MobileNavigationControls />
            </div>
          )}

          {/* Moves and Engine Content */}
          {(moves.length > 0 || isPuzzle() || isLesson() || isPuzzleMode || tags.some(tag => tag.value === 'Puzzle')) && (
            <div style={{
              border: '1px solid #444',
              borderRadius: '10px',
              padding: '4px',
              backgroundColor: '#0a0a0a',
              width: '100%'
            }}>
              <MobileMovesEngine 
                  showStockfish={true}
                  stockfishEvaluation={null}
                  height={120}
                  gameHasPuzzleTag={isPuzzle()}
                  gameHasLessonTag={isLesson()}
                  hideTabsOnAddGame={true}
                  isOnAddGame={true}
                  onActiveColorChange={(color) => {
                    setMobileActiveColor(color);
                  }}
                  onMobileArrowModeChange={(isArrowMode) => {
                    setMobileArrowMode(isArrowMode);
                  }}
                  onMobileArrowColorChange={(color) => {
                    setMobileArrowColor(color);
                  }}
                  onMobileArrowStartChange={(startSquare) => {
                    setMobileArrowStart(startSquare);
                  }}
                  onCommentMoveContentChange={handleCommentMoveContentChange}
              boardRef={boardRef}
            />
            </div>
          )}

          {/* Conditional Save & Share button when moves > 0 */}
          {state.moves.length > 0 && (
            <div style={{
              padding: '10px',
              display: 'flex',
              justifyContent: 'center'
            }}>
              <button 
                style={{
                  backgroundColor: 'gold',
                  color: 'black',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '8px 20px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  minWidth: '150px',
                  justifyContent: 'center'
                }}
                onClick={() => setActiveTab('save')}
              >
                Caption & Share <i className="fa fa-paper-plane" style={{ fontSize: '12px' }}></i>
              </button>
            </div>
          )}
            
           
            
          {/* Create Chess Content prompt when no moves AND not puzzle mode AND not lesson mode */}
          {moves.length === 0 && !isPuzzle() && !isLesson() && (
            <div style={{ 
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'space-around',
              gap: '8px',
              border: '1px solid #444',
              borderRadius: '10px',
              padding: '16px',
              backgroundColor: '#0a0a0a',
              height: '245px'
            }}>
              <div style={{
                color: 'gold',
                fontSize: '22px',
                textAlign: 'center',
                lineHeight: '1.4',
                fontWeight: 'bold',

              }}>
                Create Chess Content<br />
              </div>
                

              <div style={{ fontStyle: 'italic', color: '#ccc', fontWeight: 'normal', fontSize: '15px', textAlign: 'center', lineHeight: '18px', display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'start' }}>
             <span> <i class="fa-solid fa-chess-knight" style={{ background: '#58b047', padding: '4px 6px', borderRadius: '5px', color: 'rgb(10, 10, 10)',  marginRight: '4px' }}></i> Make moves to build the main line </span>
             <span>     <i class="fa-solid fa-font" style={{ background: '#ff0050', padding: '4px 6px', borderRadius: '5px', color: 'rgb(10, 10, 10)', marginRight: '4px' }}></i> Add variations, annotations, & arrows  <br/> </span>
             <span>   <i class="fa-solid fa-robot" style={{ background: 'gold', padding: '4px', borderRadius: '5px', color: 'rgb(10, 10, 10)', marginRight: '4px' }}></i> Analyze with Stockfish </span> 
             <span>   <i class="fa-solid fa-paper-plane" style={{ background: 'transparent', padding: '4px 6px', borderRadius: '5px', color: 'gold', marginRight: '4px' }}></i> Caption & share on ChessThread </span>
             </div>


                {/* Two Button Row */}
                <div style={{
                  display: 'flex',
                  gap: '10px',
                  justifyContent: 'center',
                  marginTop: '10px'
                }}>
                  <button
                    onClick={handleCreatePuzzleClick}
                    style={{
                      padding: '10px 16px',
                      backgroundColor: '#ff0050',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '20px',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    Create a Puzzle <i className="fa-solid fa-puzzle-piece"></i>
                  </button>
                  <button
                    onClick={() => {
                      setShowPGNUploadModal(true);
                    }}
                    style={{
                      padding: '10px 16px',
                      backgroundColor: 'gold',
                      color: 'black',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '20px',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    Upload a PGN <i className="fa-solid fa-file-import"></i>
                  </button>
                </div>
            </div>
          )}
        </div>
      </div>

      {/* Share Modal */}
      {activeTab === 'save' && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          zIndex: 10000,
          display: 'flex',
          flexDirection: 'column',
          paddingTop: 'env(safe-area-inset-top)',
          paddingBottom: 'env(safe-area-inset-bottom)'
        }}>
          <div style={{
            backgroundColor: '#1a1a1a',
            margin: '20px',
            borderRadius: '8px',
            flex: 1,
            overflow: 'auto',
            WebkitOverflowScrolling: 'touch',
            padding: '20px',
            position: 'relative'
          }}>
            {/* Close button */}
            <button
              onClick={() => setActiveTab('board')}
              style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                backgroundColor: 'transparent',
                border: 'none',
                color: '#ff0050',
                fontSize: '24px',
                cursor: 'pointer',
                padding: '5px',
                zIndex: 1
              }}
            >
              ✕
            </button>

            {shareStep === 1 && (
              <div>
                {/* Step 1 Header */}
                <h3 style={{
                  color: 'gold',
                  fontSize: '20px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  marginBottom: '20px',
                  marginTop: '0'
                }}>
                  Add Caption & Tags
                </h3>

            {/* Title Input */}
            <div style={{ marginBottom: '10px' }}>
              <label style={{ 
                color: gameCaption.length >= 2 ? 'white' : '#ff0050',
                fontSize: '16px',
                fontWeight: 'bold',
                display: 'block',
                marginBottom: '5px'
              }}>
                Caption:
              </label>
              <input
                type="text"
                value={gameCaption}
                onChange={(e) => setGameCaption(cleanProfanity(e.target.value))}
                placeholder="Minimum 2 Characters..."
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  fontSize: '16px',
                  backgroundColor: 'transparent',
                  color: 'white',
                  border: '2px solid #444',
                  borderRadius: '8px',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {/* Tags Selection */}
            <div style={{ marginBottom: '10px' }}>
              <label style={{ 
                color: tags.length > 0 ? 'white' : '#ff0050',
                fontSize: '16px',
                fontWeight: 'bold',
                display: 'block',
                marginBottom: '5px'
              }}>
                Tags:
              </label>
              <Select
                isMulti
                options={Object.entries(TAGS)
                  .filter(([key]) => key !== 'Puzzle' && key !== 'Challenge')
                  .map(([key, { label, color, background, fontWeight }]) => ({
                    value: key,
                    label: label,
                    color: color,
                    background: background,
                    fontWeight: fontWeight
                  }))}
                value={tags}
                onChange={(newTags) => {
                  if (newTags && newTags.length <= 2) {
                    setTags(newTags);
                  }
                }}
                placeholder="Select 1-2 tags..."
                styles={{
                  control: (base) => ({
                    ...base,
                    backgroundColor: 'transparent',
                    borderColor: '#444',
                    border: '2px solid #444',
                    borderRadius: '8px',
                    minHeight: '48px',
                    fontSize: '16px',
                    ':hover': {
                      borderColor: '#555'
                    }
                  }),
                  menu: (base) => ({
                    ...base,
                    backgroundColor: '#2a2a2a',
                    border: '2px solid #444',
                    borderRadius: '8px',
                    marginTop: '4px'
                  }),
                  option: (base, { data, isFocused, isSelected }) => ({
                    ...base,
                    background: isSelected 
                      ? data.background || '#e0e0e0'
                      : isFocused 
                        ? '#f5f5f5'
                        : data.background || 'transparent',
                    backgroundColor: data.background?.includes('gradient') ? 'transparent' : (isSelected 
                      ? data.background || '#e0e0e0'
                      : isFocused 
                        ? '#f5f5f5'
                        : data.background || 'transparent'),
                    color: data.color || '#000000',
                    fontWeight: data.fontWeight || 'normal',
                    cursor: 'pointer',
                    ':hover': {
                      background: data.background || '#f5f5f5',
                      backgroundColor: data.background?.includes('gradient') ? 'transparent' : (data.background || '#f5f5f5')
                    }
                  }),
                  singleValue: (base, { data }) => ({
                    ...base,
                    color: data.color || '#000000'
                  }),
                  multiValue: (base, { data }) => ({
                    ...base,
                    background: data.background || '#e0e0e0',
                    backgroundColor: data.background?.includes('gradient') ? 'transparent' : (data.background || '#e0e0e0'),
                    color: data.color || '#000000',
                    borderRadius: '4px',
                    margin: '2px'
                  }),
                  multiValueLabel: (base, { data }) => ({
                    ...base,
                    color: data.color || '#000000',
                    fontWeight: data.fontWeight || 'normal',
                    padding: '2px 6px'
                  }),
                  multiValueRemove: (base, { data }) => ({
                    ...base,
                    color: data.color || '#000000',
                    ':hover': {
                      backgroundColor: 'rgba(255, 0, 0, 0.2)',
                      color: '#ff0000'
                    }
                  }),
                  placeholder: (base) => ({
                    ...base,
                    color: '#888'
                  }),
                  input: (base) => ({
                    ...base,
                    color: 'white'
                  })
                }}
              />
            </div>

                {/* No moves warning */}
                {(!moves || moves.length === 0) && (
                  <p style={{
                    color: '#ff0050',
                    fontSize: '14px',
                    textAlign: 'center',
                    fontStyle: 'italic',
                    marginTop: '20px',
                    marginBottom: '10px',
                    padding: '10px',
                    backgroundColor: 'rgba(255, 0, 80, 0.1)',
                    borderRadius: '6px',
                    border: '1px solid rgba(255, 0, 80, 0.3)'
                  }}>
                    <i className="fa-solid fa-info-circle" style={{ marginRight: '8px' }}></i>
                    Make moves on the chessboard
                  </p>
                )}

                {/* More Settings Link */}
                <button
                  onClick={() => setShareStep(2)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#888',
                    fontSize: '15px',
                    cursor: 'pointer',
                    padding: '8px 0',
                    marginTop: '20px',
                    textAlign: 'center',
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    transition: 'color 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.color = '#aaa'}
                  onMouseOut={(e) => e.currentTarget.style.color = '#888'}
                >
                  <span>More Settings</span>
                  <i className="fa-solid fa-plus" style={{ fontSize: '12px' }}></i>
                </button>

                {/* Create ChessThread Button - Now submits directly */}
                <button
                  onClick={onSubmitGame}
                  disabled={!isFormValid()}
                  style={{
                    width: '100%',
                    padding: '16px 20px',
                    backgroundColor: 'gold',
                    color: 'black',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '18px',
                    fontWeight: 'bold',
                    cursor: isFormValid() ? 'pointer' : 'not-allowed',
                    marginTop: '12px',
                    boxShadow: '0 2px 8px rgba(255, 215, 0, 0.3)',
                    transition: 'all 0.2s ease',
                    opacity: isFormValid() ? 1 : 0.5,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseDown={(e) => {
                    if (isFormValid()) e.target.style.transform = 'scale(0.98)';
                  }}
                  onMouseUp={(e) => {
                    if (isFormValid()) e.target.style.transform = 'scale(1)';
                  }}
                  onMouseLeave={(e) => {
                    if (isFormValid()) e.target.style.transform = 'scale(1)';
                  }}
                >
                  <span>Share to ChessThread</span>
                </button>
              </div>
            )}

            {shareStep === 2 && (
              <div>
                {/* Step 2 Header with Back Button */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  position: 'relative',
                  marginBottom: '20px'
                }}>
                  {/* Back Button */}
                  <button
                    onClick={() => setShareStep(1)}
                    style={{
                      position: 'absolute',
                      left: '0',
                      top: '40%',
                      transform: 'translateY(-50%)',
                      backgroundColor: 'transparent',
                      border: 'none',
                      color: '#ff0050',
                      fontSize: '20px',
                      cursor: 'pointer',
                      padding: '4px',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.color = '#ff3366';
                      e.target.style.transform = 'translateY(-50%) scale(1.1)';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.color = '#ff0050';
                      e.target.style.transform = 'translateY(-50%) scale(1)';
                    }}
                  >
                    <i className="fa-solid fa-arrow-left"></i>
                  </button>

                  {/* Header Title */}
                  <h3 style={{
                    color: 'gold',
                    fontSize: '20px',
                    fontWeight: 'bold',
                    textAlign: 'center',
                    margin: '0',
                    flex: 1
                  }}>
                    Optional Settings
                  </h3>
                </div>

            {/* Difficulty Selection */}
            <div style={{ marginBottom: '15px' }}>
              {!showDifficultyField ? (
                <button
                  onClick={() => setShowDifficultyField(true)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#888',
                    fontSize: '15px',
                    cursor: 'pointer',
                    padding: '8px 0',
                    textAlign: 'left',
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'color 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.color = '#aaa'}
                  onMouseOut={(e) => e.currentTarget.style.color = '#888'}
                >
                  <i className="fa-solid fa-plus" style={{ fontSize: '12px' }}></i>
                  Set Difficulty
                </button>
              ) : (
                <div>
                  <label style={{ 
                    color: 'white',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    display: 'block',
                    marginBottom: '5px'
                  }}>
                    Level:
                  </label>
                  <select
                    value={selectedDifficulty}
                    onChange={(e) => handleDifficultyChange(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      fontSize: '16px',
                      backgroundColor: 'transparent',
                      color: 'white',
                      border: '2px solid #444',
                      borderRadius: '8px',
                      boxSizing: 'border-box'
                    }}
                  >
                    {Object.entries(DIFFICULTY).map(([key, difficultyObj]) => (
                      <option key={key} value={difficultyObj.label}>{difficultyObj.label}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Player Names and Ratings */}
            <div style={{ marginBottom: '15px' }}>
              {/* White Player */}
              {!showWhitePlayerField ? (
                <button
                  onClick={() => setShowWhitePlayerField(true)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#888',
                    fontSize: '15px',
                    cursor: 'pointer',
                    padding: '8px 0',
                    textAlign: 'left',
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'color 0.2s',
                    marginBottom: '8px'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.color = '#aaa'}
                  onMouseOut={(e) => e.currentTarget.style.color = '#888'}
                >
                  <i className="fa-solid fa-plus" style={{ fontSize: '12px' }}></i>
                  Add White Player
                </button>
              ) : (
                <div style={{ marginBottom: '8px' }}>
                  <label style={{ color: '#ccc', fontSize: '14px', marginBottom: '3px', display: 'block' }}>
                    White Player:
                  </label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="text"
                      value={getWhitePlayerData()[0] || ''}
                      onChange={(e) => setWhitePlayerData(e.target.value, getWhitePlayerData()[1] || '')}
                      placeholder="Name"
                      style={{
                        flex: 1,
                        padding: '10px 12px',
                        fontSize: '15px',
                        backgroundColor: 'transparent',
                        color: 'white',
                        border: '2px solid #444',
                        borderRadius: '6px',
                        boxSizing: 'border-box'
                      }}
                    />
                    <input
                      type="number"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={getWhitePlayerData()[1] || ''}
                      onChange={(e) => setWhitePlayerData(getWhitePlayerData()[0] || '', e.target.value)}
                      placeholder="1200"
                      min="0"
                      max="9999"
                      style={{
                        width: '80px',
                        padding: '10px 8px',
                        fontSize: '15px',
                        backgroundColor: 'transparent',
                        color: 'white',
                        border: '2px solid #444',
                        borderRadius: '6px',
                        boxSizing: 'border-box',
                        textAlign: 'center'
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Black Player */}
              {!showBlackPlayerField ? (
                <button
                  onClick={() => setShowBlackPlayerField(true)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#888',
                    fontSize: '15px',
                    cursor: 'pointer',
                    padding: '8px 0',
                    textAlign: 'left',
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'color 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.color = '#aaa'}
                  onMouseOut={(e) => e.currentTarget.style.color = '#888'}
                >
                  <i className="fa-solid fa-plus" style={{ fontSize: '12px' }}></i>
                  Add Black Player
                </button>
              ) : (
                <div>
                  <label style={{ color: '#ccc', fontSize: '14px', marginBottom: '3px', display: 'block' }}>
                    Black Player:
                  </label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="text"
                      value={getBlackPlayerData()[0] || ''}
                      onChange={(e) => setBlackPlayerData(e.target.value, getBlackPlayerData()[1] || '')}
                      placeholder="Name"
                      style={{
                        flex: 1,
                        padding: '10px 12px',
                        fontSize: '15px',
                        backgroundColor: 'transparent',
                        color: 'white',
                        border: '2px solid #444',
                        borderRadius: '6px',
                        boxSizing: 'border-box'
                      }}
                    />
                    <input
                      type="number"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={getBlackPlayerData()[1] || ''}
                      onChange={(e) => setBlackPlayerData(getBlackPlayerData()[0] || '', e.target.value)}
                      placeholder="1200"
                      min="0"
                      max="9999"
                      style={{
                        width: '80px',
                        padding: '10px 8px',
                        fontSize: '15px',
                        backgroundColor: 'transparent',
                        color: 'white',
                        border: '2px solid #444',
                        borderRadius: '6px',
                        boxSizing: 'border-box',
                        textAlign: 'center'
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Bookmark Selection */}
            <div style={{ marginBottom: '10px' }}>
              {!showBookmarkField ? (
                <button
                  onClick={() => setShowBookmarkField(true)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#888',
                    fontSize: '15px',
                    cursor: 'pointer',
                    padding: '8px 0',
                    textAlign: 'left',
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'color 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.color = '#aaa'}
                  onMouseOut={(e) => e.currentTarget.style.color = '#888'}
                >
                  <i className="fa-solid fa-plus" style={{ fontSize: '12px' }}></i>
                  Bookmark
                </button>
              ) : (
                <div>
                  <label style={{ 
                    color: 'white',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    display: 'block',
                    marginBottom: '5px'
                  }}>
                    Bookmark:
                  </label>
                  {!isCreatingNewFolder ? (
                    <select
                      value={selectedBookmarkFolder}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === 'NEW_FOLDER') {
                          setIsCreatingNewFolder(true);
                        } else {
                          setSelectedBookmarkFolder(value);
                        }
                      }}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        fontSize: '16px',
                        backgroundColor: 'transparent',
                        color: 'white',
                        border: '2px solid #444',
                        borderRadius: '8px',
                        boxSizing: 'border-box'
                      }}
                    >
                      <option value="NONE">No bookmark</option>
                      {userFolders.map(folder => (
                        <option key={folder.id} value={folder.name}>
                          {folder.name} {folder.isPrivate ? '🔒' : ''}
                        </option>
                      ))}
                      <option value="NEW_FOLDER">+ Create new folder</option>
                    </select>
                  ) : (
                    <div>
                      <input
                        type="text"
                        value={newFolderName}
                        onChange={(e) => setNewFolderName(e.target.value)}
                        placeholder="New folder name..."
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          fontSize: '16px',
                          backgroundColor: 'transparent',
                          color: 'white',
                          border: '2px solid #444',
                          borderRadius: '8px',
                          marginBottom: '10px',
                          boxSizing: 'border-box'
                        }}
                      />
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <button
                          onClick={() => {
                            setIsCreatingNewFolder(false);
                            setSelectedBookmarkFolder('NONE');
                          }}
                          style={{
                            flex: 1,
                            padding: '10px',
                            backgroundColor: '#333',
                            color: 'white',
                            border: 'none',
                            borderRadius: '5px'
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

                {/* Bookmark Notification */}
                {bookmarkNotification && (
                  <div style={{
                    padding: '10px',
                    marginBottom: '10px',
                    borderRadius: '5px',
                    backgroundColor: bookmarkNotification.type === 'success' ? '#57b047' : '#ff0050',
                    color: 'white',
                    textAlign: 'center',
                    fontSize: '14px'
                  }}>
                    {bookmarkNotification.message}
                  </div>
                )}

                {/* Submit button removed - submission now happens from Step 1 */}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Annotation Popup Modal */}
      {showAnnotationPopup && (
        <>
          {/* Background overlay */}
          <div
            onClick={handleAnnotationCancel}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              zIndex: 1000
            }}
          />
          
          {/* Modal content */}
          <div style={{
            position: 'fixed',
            top: isNativeApp() ? 'calc(10% + 60px)' : '10%', // Account for native app header
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: '#1a1a1a',
            border: '1px solid #333',
            borderRadius: '12px',
            padding: '20px',
            zIndex: 1001,
            width: '85%',
            maxWidth: '380px',
            maxHeight: '80vh',
            boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '15px'
            }}>
              <h3 style={{ margin: 0, color: 'white', fontSize: '18px' }}>Move Annotation</h3>
              <button
                onClick={handleAnnotationCancel}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: '#888',
                  fontSize: '20px',
                  cursor: 'pointer'
                }}
              >
                <i className="fa-solid fa-times"></i>
              </button>
            </div>
            
            <textarea
              value={popupContent}
              onChange={(e) => setPopupContent(e.target.value)}
              placeholder="Add your annotation for this move..."
              autoFocus
              rows={8}
              style={{
                width: '100%',
                minHeight: '150px',
                maxHeight: '50vh',
                padding: '12px 16px',
                backgroundColor: '#ffffff',
                border: '2px solid #ccc',
                borderRadius: '8px',
                color: '#000000',
                fontSize: '16px',
                resize: 'vertical',
                outline: 'none',
                marginBottom: '20px',
                boxSizing: 'border-box',
                lineHeight: '1.4',
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }}
            />
            
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'space-between',
              marginTop: 'auto'
            }}>
              <button
                onClick={handleAnnotationCancel}
                style={{
                  flex: 1,
                  padding: '14px 20px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseDown={(e) => e.target.style.backgroundColor = '#5a6268'}
                onMouseUp={(e) => e.target.style.backgroundColor = '#6c757d'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#6c757d'}
              >
                Cancel
              </button>
              <button
                onClick={handleAnnotationSave}
                style={{
                  flex: 1,
                  padding: '14px 20px',
                  backgroundColor: 'gold',
                  color: 'black',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(255, 215, 0, 0.3)',
                  transition: 'transform 0.2s ease'
                }}
                onMouseDown={(e) => e.target.style.transform = 'scale(0.98)'}
                onMouseUp={(e) => e.target.style.transform = 'scale(1)'}
                onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
              >
                Save
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Connect to Redux to get user data
const mapStateToProps = (state) => ({
  user: state.user
});

const ConnectedMobileAddGameContent = connect(mapStateToProps)(MobileAddGameContent);

// Mobile wrapper that provides context
const MobileAddGame = () => {
  return (
    <GameStateProvider>
      <ConnectedMobileAddGameContent />
    </GameStateProvider>
  );
};

export default MobileAddGame;